import React, { FC, useEffect, useState } from "react"
import { ArtistTrendEconomyData, ArtistTrendEconomy, RoyaltyChartData } from "../../../../types"
import { KButton, KSpan, KTitleSpan, KDropdown } from "kahuna-base-react-components"
import NotFound from "../../../NotFound"
import { lang } from "../../../../constants/languages"
import { formatNumber, replaceCurrencies } from "../../../../utility"
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
} from "chart.js"
import { Line } from "react-chartjs-2"
import { ARTIST_TREND_ECONOMY_ROYALTY_TYPE, chartLabels } from "../../../../constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)

type RoyaltyThreeMonthsProps = {
  artistTrendEconomyData: ArtistTrendEconomyData
  setOffset: (offset: number) => void
  offset: number
  limit: number
  selectedRoyaltyType?: KSelectOption
  setSelectedRoyaltyType: (option?: KSelectOption) => void
  selectedResults: ArtistTrendEconomy[]
  setSelectedResults: (results: ArtistTrendEconomy[]) => void
  hasNext: boolean
}

const RoyaltyThreeMonths: FC<RoyaltyThreeMonthsProps> = (props) => {
  const { artistTrendEconomyData, offset, setOffset, limit, selectedResults, setSelectedResults, hasNext } = props

  const [hoverId, setHoverId] = useState<number>()
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [selectedCountries, setSelectedCountries] = useState<KSelectOption[]>([])

  const royaltyTypeOptions: KSelectOption[] = [
    { label: "Advance Deals", value: 1, value2: ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE },
    { label: "Distribution Deals", value: 2, value2: ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION }
  ]

  const platformOptions: KSelectOption[] = Object.entries(chartLabels.platform).map(([key, value], index) => {
    return { label: value, value: index, value2: key }
  })

  const countryOptions: KSelectOption[] = Object.entries(chartLabels.storeFront).map(([key, value], index) => {
    return { label: value, value: index, value2: key }
  })

  const getLast3MonthsLabels = () => {
    const labels: string[] = []
    const today = new Date()
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

    for (let i = 2; i >= 0; i--) {
      // oldest first
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1)
      const month = monthNames[date.getMonth()]
      const year = date.getFullYear()
      labels.push(`${month} ${year}`)
    }

    return labels
  }
  const last30 = artistTrendEconomyData.totals.reduce((s, u) => s + u.totalAmount30, 0)
  const last60 = artistTrendEconomyData.totals.reduce((s, u) => s + u.totalAmount60, 0)
  const last90 = artistTrendEconomyData.totals.reduce((s, u) => s + u.totalAmount90, 0)

  const dynamicLabels = getLast3MonthsLabels() // e.g., ["202510", "202511", "202512"]

  // Original chart labels and data
  const originalLabels = artistTrendEconomyData.chartData.map((item) => item.monthLabel)
  const originalData = artistTrendEconomyData.chartData.map((item) => item.royalty)

  // Ensure labels cover last 3 months
  const allLabels = [...new Set([...originalLabels, ...dynamicLabels])]

  const mappedOriginalData = allLabels.map((label) => {
    const index = originalLabels.indexOf(label)
    return index !== -1 ? originalData[index] : 0 // fill 0 if new month
  })

  const totalLineData = allLabels.map((label) => {
    if (label === dynamicLabels[2]) return last30
    if (label === dynamicLabels[1]) return last60
    if (label === dynamicLabels[0]) return last90
    return 0 // or leave 0 for older months
  })

  const lineChartData = {
    labels: allLabels,
    datasets: [
      {
        label: "USD", // existing line
        data: mappedOriginalData,
        borderColor: "#56E099",
        backgroundColor: "rgba(86, 224, 153, 0.1)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: "#56E099"
      },
      {
        label: "Total Amount (Last 3 Months)", // new line
        data: totalLineData,
        borderColor: "#b5f2ffff",
        backgroundColor: "rgba(133, 221, 247, 0.1)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: "#b5f2ffff"
      }
    ]
  }

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        callbacks: {
          label: (context: any) => `USD: ${formatNumber(context.raw.toFixed(2))}`
        }
      },
      datalabels: { display: false }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#666" }
      },
      y: {
        grid: { color: "#f0f0f0" },
        ticks: {
          color: "#666",
          callback: (value: any) => formatNumber(value)
        },
        beginAtZero: true
      }
    }
  }

  // @ts-ignore
  const sumBy = (list: ArtistTrendEconomy[] = [], key: string) => list.reduce((sum, item) => sum + (item[key] || 0), 0)

  const formatPair = (amount: number, net: number) =>
    `${formatNumber(amount.toFixed(1))}$ / ${formatNumber(net.toFixed(1))}$`

  const formatDiff = (amount: number, net: number) => `+${formatNumber((amount - net).toFixed(1))}$`

  const renderRoyaltiesDetail = (artistTrendEconomy: ArtistTrendEconomy, marginTop: number) => {
    return (
      <div style={{ width: "35%", marginTop }}>
        <div className="flex">
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount30NewRelease || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet30NewRelease || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount30NewRelease - artistTrendEconomy.totalNet30NewRelease).toFixed(1)
                )}$`}
              />
            </div>
          </div>
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount30BackCatalog || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet30BackCatalog || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount30BackCatalog - artistTrendEconomy.totalNet30BackCatalog).toFixed(1)
                )}$`}
              />
            </div>
          </div>
          <div className="flex flex-col" style={{ width: "30%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount30Takedown || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet30Takedown || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount30Takedown - artistTrendEconomy.totalNet30Takedown).toFixed(1)
                )}$`}
              />
            </div>
          </div>
        </div>
        <div className="flex mt-2">
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount60NewRelease || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet60NewRelease || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount60NewRelease - artistTrendEconomy.totalNet60NewRelease).toFixed(1)
                )}$`}
              />
            </div>
          </div>
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount60BackCatalog || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet60BackCatalog || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount60BackCatalog - artistTrendEconomy.totalNet60BackCatalog).toFixed(1)
                )}$`}
              />
            </div>
          </div>
          <div className="flex flex-col" style={{ width: "30%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount60Takedown || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet60Takedown || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount60Takedown - artistTrendEconomy.totalNet60Takedown).toFixed(1)
                )}$`}
              />
            </div>
          </div>
        </div>
        <div className="flex mt-2">
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount90NewRelease || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet90NewRelease || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount90NewRelease - artistTrendEconomy.totalNet90NewRelease).toFixed(1)
                )}$`}
              />
            </div>
          </div>
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount90BackCatalog || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet90BackCatalog || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount90BackCatalog - artistTrendEconomy.totalNet90BackCatalog).toFixed(1)
                )}$`}
              />
            </div>
          </div>
          <div className="flex flex-col" style={{ width: "30%" }}>
            <KSpan
              text={`${formatNumber((artistTrendEconomy.totalAmount90Takedown || 0).toFixed(1))}$ / ${formatNumber(
                (artistTrendEconomy.totalNet90Takedown || 0).toFixed(1)
              )}$`}
            />
            <div className="flex ml-4">
              <KSpan
                fontWeight={600}
                color="green"
                text={`+${formatNumber(
                  (artistTrendEconomy.totalAmount90Takedown - artistTrendEconomy.totalNet90Takedown).toFixed(1)
                )}$`}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderPlatforms = (platform?: string | string[], currency?: string) => {
    if (!platform) {
      return <div />
    }

    const platformsArray = Array.isArray(platform) ? platform : [platform]

    const mergedPlatform = platformsArray.reduce<Record<string, number>>((acc, platformStr) => {
      if (!platformStr) return acc

      let parsed: Record<string, number> = {}

      try {
        parsed = JSON.parse(platformStr)
      } catch (e) {
        console.error("Invalid platform JSON", e)
        return acc
      }

      Object.entries(parsed).forEach(([key, value]) => {
        acc[key] = (acc[key] || 0) + Number(value)
      })

      return acc
    }, {})

    const selectedPlatformKeys = selectedPlatforms.map((p) => p.value2)
    const sumPlatforms = Object.values(mergedPlatform).reduce((a, b) => a + b, 0) || 1

    return (
      <div>
        {Object.entries(mergedPlatform)
          .filter(([key, value]) => selectedPlatformKeys.length === 0 || selectedPlatformKeys.includes(key))
          .map(([key, value]) => (
            <div key={key} className="flex gap-1">
              <KSpan fontWeight={600} color="#111" text={`${key}: `} />
              <KSpan text={`${((value / sumPlatforms) * 100).toFixed(1)}%`} />
            </div>
          ))}
      </div>
    )
  }

  const renderCountries = (paddingTop: number, country?: string | string[], currency?: string) => {
    if (!country) {
      return <div />
    }

    const countryArray = Array.isArray(country) ? country : [country]

    const mergedCountries = countryArray.reduce<
      Record<string, { royalty_30: number; royalty_60: number; royalty_90: number }>
    >((acc, countryStr) => {
      if (!countryStr) return acc

      let parsed: Record<string, any> = {}

      try {
        parsed = JSON.parse(countryStr)
      } catch (e) {
        console.error("Invalid country JSON", e)
        return acc
      }

      Object.entries(parsed).forEach(([countryCode, values]: any) => {
        if (!acc[countryCode]) {
          acc[countryCode] = {
            royalty_30: 0,
            royalty_60: 0,
            royalty_90: 0
          }
        }

        acc[countryCode].royalty_30 += Number(values.royalty_30 || 0)
        acc[countryCode].royalty_60 += Number(values.royalty_60 || 0)
        acc[countryCode].royalty_90 += Number(values.royalty_90 || 0)
      })

      return acc
    }, {})

    const selectedCountryKeys = selectedCountries.map((p) => p.value2)
    const sumCountries90 = Object.values(mergedCountries).reduce((a, b) => a + b.royalty_90, 0) || 1
    const sumCountries60 = Object.values(mergedCountries).reduce((a, b) => a + b.royalty_60, 0) || 1
    const sumCountries30 = Object.values(mergedCountries).reduce((a, b) => a + b.royalty_30, 0) || 1

    return (
      <div style={{ width: "42%", paddingTop }}>
        {Object.entries(mergedCountries)
          .filter(
            ([countryCode, values]) => selectedCountryKeys.length === 0 || selectedCountryKeys.includes(countryCode)
          )
          .map(([countryCode, values]) => (
            <div key={countryCode} className="flex" style={{ height: 19.7 }}>
              {/* Country */}
              <div style={{ width: "13%" }}>
                <KSpan fontWeight={600} color="#111" text={`${countryCode}:`} />
              </div>

              {/* 90 */}
              <div style={{ width: "30%" }}>
                <KSpan text={`${((values.royalty_90 / sumCountries90) * 100).toFixed(1)}%`} />
              </div>

              {/* 60 */}
              <div style={{ width: "30%" }}>
                <KSpan text={`${((values.royalty_60 / sumCountries60) * 100).toFixed(1)}%`} />
              </div>

              {/* 30 */}
              <div style={{ width: "27%" }}>
                <KSpan text={`${((values.royalty_30 / sumCountries30) * 100).toFixed(1)}%`} />
              </div>
            </div>
          ))}
      </div>
    )
  }

  const DealColumn = ({
    results,
    amountKey,
    netKey
  }: {
    results: ArtistTrendEconomy[]
    amountKey: string
    netKey: string
  }) => {
    const amount = sumBy(results, amountKey)
    const net = sumBy(results, netKey)

    return (
      <div style={{ width: "13%" }}>
        <KSpan fontWeight={600} color="#111" text={formatPair(amount, net)} />
        <div className="flex ml-4">
          <KSpan fontWeight={600} color="green" text={formatDiff(amount, net)} />
        </div>
      </div>
    )
  }

  const onChangeRoyaltyType = (selected: KSelectOption | undefined) => {
    setOffset(0)
    setSelectedResults([])
    props.setSelectedRoyaltyType(selected as KSelectOption)
  }

  const DealRow = ({ title, royaltyType }: { title: string; royaltyType?: string }) => {
    const periods = [
      { amount: "totalAmount90", net: "totalNet90" },
      { amount: "totalAmount60", net: "totalNet60" },
      { amount: "totalAmount30", net: "totalNet30" }
    ]

    let results: ArtistTrendEconomy[] = artistTrendEconomyData.totals
    if (royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE) {
      results = artistTrendEconomyData.totals.filter((t) => t.royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE)
    } else if (royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION) {
      results = artistTrendEconomyData.totals.filter(
        (t) => t.royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION
      )
    }

    const mergedResult: ArtistTrendEconomy | undefined = results.reduce((acc, curr) => {
      if (!acc) {
        return { ...curr }
      }

      return {
        ...acc,
        // repeat for new_release / back_catalog / takedown if present
        totalNet30NewRelease: acc.totalNet30NewRelease + curr.totalNet30NewRelease,
        totalNet60NewRelease: acc.totalNet60NewRelease + curr.totalNet60NewRelease,
        totalNet90NewRelease: acc.totalNet90NewRelease + curr.totalNet90NewRelease,
        totalAmount30NewRelease: acc.totalAmount30NewRelease + curr.totalAmount30NewRelease,
        totalAmount60NewRelease: acc.totalAmount60NewRelease + curr.totalAmount60NewRelease,
        totalAmount90NewRelease: acc.totalAmount90NewRelease + curr.totalAmount90NewRelease,
        totalNet30BackCatalog: acc.totalNet30BackCatalog + curr.totalNet30BackCatalog,
        totalNet60BackCatalog: acc.totalNet60BackCatalog + curr.totalNet60BackCatalog,
        totalNet90BackCatalog: acc.totalNet90BackCatalog + curr.totalNet90BackCatalog,
        totalAmount30BackCatalog: acc.totalAmount30BackCatalog + curr.totalAmount30BackCatalog,
        totalAmount60BackCatalog: acc.totalAmount60BackCatalog + curr.totalAmount60BackCatalog,
        totalAmount90BackCatalog: acc.totalAmount90BackCatalog + curr.totalAmount90BackCatalog,
        totalNet30Takedown: acc.totalNet30Takedown + curr.totalNet30Takedown,
        totalNet60Takedown: acc.totalNet60Takedown + curr.totalNet60Takedown,
        totalNet90Takedown: acc.totalNet90Takedown + curr.totalNet90Takedown,
        totalAmount30Takedown: acc.totalAmount30Takedown + curr.totalAmount30Takedown,
        totalAmount60Takedown: acc.totalAmount60Takedown + curr.totalAmount60Takedown,
        totalAmount90Takedown: acc.totalAmount90Takedown + curr.totalAmount90Takedown
      }
    }, undefined as ArtistTrendEconomy | undefined)

    return (
      <div className="p-2 mb-3" style={{ width: "100%", background: "#00000008", borderRadius: "5px" }}>
        <div className="flex" style={{ width: "100%" }}>
          <div style={{ width: "26%" }}>
            <div className="p-1">
              <KSpan fontWeight={600} color="#111" text={title} />
            </div>
          </div>

          {periods.map((p) => (
            <DealColumn key={p.amount} results={results} amountKey={p.amount} netKey={p.net} />
          ))}

          <div className="flex flex-col" style={{ width: "12%" }}>
            <KSpan color="#111" fontWeight={600} text={`New Release`} />
            <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
          </div>
          <div className="flex flex-col" style={{ width: "12%" }}>
            <KSpan color="#111" fontWeight={600} text={`Back Catalog`} />
            <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
          </div>
          <div className="flex flex-col" style={{ width: "10%" }}>
            <KSpan color="#111" fontWeight={600} text={`Takedown`} />
            <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
          </div>
        </div>
        <div className="flex" style={{ width: "100%" }}>
          <div style={{ width: "23%" }}>
            <div className="p-1 pt-3">
              {renderPlatforms(
                results.map((r) => r.platform || ""),
                "USD"
              )}
            </div>
          </div>
          {renderCountries(
            10,
            results.map((r) => r.country || ""),
            "USD"
          )}
          {mergedResult && renderRoyaltiesDetail(mergedResult, 14)}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col gap-4">
      {/* Chart Section */}
      <div
        className="py-5 px-7"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div className="flex items-center gap-4 mb-4 justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#56E099" }}></div>
            <KSpan text="Royalty Reports:" fontWeight={600} />
            <KSpan
              text={formatNumber(artistTrendEconomyData.totalRoyalty?.toFixed(2))}
              fontWeight={600}
              fontSize={16}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#45cfb8ff" }}></div>
            <KSpan text="Total:" fontWeight={600} />
            <KSpan
              text={formatNumber((last30 + last60 + artistTrendEconomyData.totalRoyalty).toFixed(2))}
              fontWeight={600}
              fontSize={16}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#b5f2ffff" }}></div>
            <KSpan text="Last 90 Days" fontWeight={600} />
            <KSpan text="(Daily Streams * Unit Price)" fontSize={12} fontWeight={500} />
            <KSpan text=":" fontWeight={500} />
            <KSpan text={formatNumber(last30 + last60 + last90)} fontWeight={600} fontSize={16} />
          </div>
        </div>
        <div style={{ height: 200 }}>
          {artistTrendEconomyData.chartData.length > 0 ? (
            <Line data={lineChartData} options={lineChartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <KSpan text="No chart data available" color="#999" />
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <KDropdown
          isClearable
          width={160}
          menuWidth={170}
          placeholder="Deal Type"
          options={royaltyTypeOptions}
          selected={props.selectedRoyaltyType}
          onSelect={(selected) => onChangeRoyaltyType(selected as KSelectOption)}
        />
        <KDropdown
          allowContainerShrink
          isClearable
          isMulti
          menuWidth={150}
          closeMenuOnSelect={false}
          placeholder="Filter Platforms"
          options={platformOptions}
          selected={selectedPlatforms}
          onBlur={(selected) => setSelectedPlatforms(selected as KSelectOption[])}
          onSelect={() => {}}
        />
        <KDropdown
          allowContainerShrink
          isClearable
          isMulti
          menuWidth={150}
          closeMenuOnSelect={false}
          placeholder="Filter Countries"
          options={countryOptions}
          selected={selectedCountries}
          onBlur={(selected) => setSelectedCountries(selected as KSelectOption[])}
          onSelect={() => {}}
        />
      </div>
      {/* Table Section */}
      <div
        className="py-5 px-7"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div>
          <div className="flex" style={{ width: "100%" }}>
            <div style={{ width: "22%" }}></div>
            <div className="flex justify-center" style={{ width: "39%" }}>
              <KSpan color="#111" text={"Gross / Net / Kahuna Share"} />
            </div>
          </div>
          <div className="flex mb-4" style={{ width: "100%" }}>
            <div style={{ width: "26%" }}>
              <KSpan color="#111" text={"User"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"60-90 days"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"30-60 days"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"0-30 days"} />
            </div>
            <div style={{ width: "12%" }}>
              <KSpan color="#111" text={"New Release"} />
            </div>
            <div style={{ width: "12%" }}>
              <KSpan color="#111" text={"Back Catalog"} />
            </div>
            <div style={{ width: "11%" }}>
              <KSpan color="#111" text={"Takedown"} />
            </div>
          </div>
          {selectedResults && !(selectedResults.length > 0) && (
            <div className="flex p-5 w-full justify-center mt-[100px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
          <DealRow title="TOTAL" royaltyType={undefined} />
          <DealRow title="DISTRIBUTION DEALS" royaltyType={ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION} />
          <DealRow title="ADVANCE DEALS" royaltyType={ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE} />

          {selectedResults?.map((artistTrendEconomy) => {
            const background = hoverId === artistTrendEconomy.userId ? "#00000008" : "transparent"
            return (
              <div
                key={`trend_top_track_${artistTrendEconomy.userId}_${artistTrendEconomy.royaltyType}_${Math.random()}`}
                onMouseOver={() => setHoverId(artistTrendEconomy.userId)}
              >
                <div className="flex p-2" style={{ width: "100%", background, borderRadius: "5px" }}>
                  <div style={{ width: "26%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                      }}
                    >
                      <img
                        src={artistTrendEconomy?.user?.photo || "/account_icons/avatar.svg"}
                        alt={artistTrendEconomy?.user?.firstName}
                        style={{ width: "50px", height: "50px", borderRadius: "50%" }}
                      />
                      <div>
                        <div>
                          <KTitleSpan
                            fontSize={16}
                            lineHeight="20px"
                            text={`${artistTrendEconomy?.user?.firstName} ${artistTrendEconomy?.user?.lastName}`}
                          />
                        </div>
                        <div>
                          <KSpan fontSize={12} text={artistTrendEconomy?.user?.email || "-"} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ width: "13%" }}>
                    <KSpan
                      text={`${formatNumber((artistTrendEconomy.totalAmount90 || 0).toFixed(1))}$ / ${formatNumber(
                        (artistTrendEconomy.totalNet90 || 0).toFixed(1)
                      )}$`}
                    />
                    <div className="flex ml-4">
                      <KSpan
                        fontWeight={600}
                        color="green"
                        text={`+${formatNumber(
                          (artistTrendEconomy.totalAmount90 - artistTrendEconomy.totalNet90).toFixed(1)
                        )}$`}
                      />
                    </div>
                  </div>
                  <div style={{ width: "13%" }}>
                    <KSpan
                      text={`${formatNumber((artistTrendEconomy.totalAmount60 || 0).toFixed(1))}$ / ${formatNumber(
                        (artistTrendEconomy.totalNet60 || 0).toFixed(1)
                      )}$`}
                    />
                    <div className="flex ml-4">
                      <KSpan
                        fontWeight={600}
                        color="green"
                        text={`+${formatNumber(
                          (artistTrendEconomy.totalAmount60 - artistTrendEconomy.totalNet60).toFixed(1)
                        )}$`}
                      />
                    </div>
                  </div>
                  <div style={{ width: "13%" }}>
                    <KSpan
                      text={`${formatNumber((artistTrendEconomy.totalAmount30 || 0).toFixed(1))}$ / ${formatNumber(
                        (artistTrendEconomy.totalNet30 || 0).toFixed(1)
                      )}$`}
                    />
                    <div className="flex ml-4">
                      <KSpan
                        fontWeight={600}
                        color="green"
                        text={`+${formatNumber(
                          (artistTrendEconomy.totalAmount30 - artistTrendEconomy.totalNet30).toFixed(1)
                        )}$`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col" style={{ width: "12%" }}>
                    <KSpan color="#111" fontWeight={600} text={`New Release`} />
                    <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
                  </div>
                  <div className="flex flex-col" style={{ width: "12%" }}>
                    <KSpan color="#111" fontWeight={600} text={`Back Catalog`} />
                    <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
                  </div>
                  <div className="flex flex-col" style={{ width: "11%" }}>
                    <KSpan color="#111" fontWeight={600} text={`Takedown`} />
                    <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
                  </div>
                </div>
                <div className="flex p-2" style={{ width: "100%", background, borderRadius: "5px" }}>
                  <div style={{ width: "23%" }}>
                    <div className="p-1 pt-1">
                      {renderPlatforms(artistTrendEconomy.platform, artistTrendEconomy.currency)}
                    </div>
                  </div>
                  {renderCountries(2, artistTrendEconomy.country, artistTrendEconomy.currency)}
                  {renderRoyaltiesDetail(artistTrendEconomy, 0)}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="pb-4">
        {hasNext && (
          <div className="flex justify-center">
            <KButton
              onClick={() => setOffset(offset + limit)}
              text="Load More"
              width="120px"
              background="transparent"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default RoyaltyThreeMonths
